// Services and React components
import { useEffect, useState } from "react";
import locationsService from "services/locations-service";

// MUI and Material Dashboard components
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import MDTypography from "components/MDTypography";

export default function LocationFields({
    inventoryItem,
    changeHandler,
    batchChangeHandler,
    commonProps,
    editMode
}) {
    const [isLoading, setIsLoading] = useState(false);

    // State holding options and filtered options for Autocomplete components
    const [locations, setLocations] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [shelfList, setShelfList] = useState([]);
    const [drawerList, setDrawerList] = useState([]);

    // State for selected values in the Autocomplete components
    const [selectedLocType, setSelectedLocType] = useState("");
    const [selectedLoc, setSelectedLoc] = useState(null);
    const [selectedShelf, setSelectedShelf] = useState(null);
    const [selectedDrawer, setSelectedDrawer] = useState(null);
    const [previousLoc, setPreviousLoc] = useState("");

    // State to manage visibility of fields
    const [openDrawerField, setOpenDrawerField] = useState(false);
    const [openShelfField, setOpenShelfField] = useState(false);
    const [openPreviousLocText, setOpenPreviousLocText] = useState(false);

    const locationTypesList = ["Lane/Rack/Shelf", "Lane/Rack/Shelf/Drawer",
        "Room", "Room/Rack/Shelf", "Room/Rack/Shelf/Drawer"];

    // Fetch locations from the API
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const response = await locationsService.getAllLocations();
                const attrs = response?.data?.attributes || [];

                // Normalize and sort locations by city so they can be grouped by city in the Autocomplete.
                const normalized = attrs
                    .map((item) => ({
                        ...item,
                        city: item.city || "",
                        locationKey: item.locationKey || item.location || "",
                    }))
                    .sort((a, b) => a.city.localeCompare(b.city));

                setLocations(normalized);

                if (editMode) {
                    initEditSelection(inventoryItem, normalized);
                }
            } catch (error) {
                console.error("Error fetching locations:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [])

    // Initialize selection state when editing: pre-select location type, location, shelf/drawer or show previous text
    const initEditSelection = (item, normalizedLocations) => {
        if (!item) return;
        if (item.locationId) {
            const type = item.locationId?.locationType || "";
            handleLocationTypeChange(type, false);

            const updatedLocations = filterLocationList(normalizedLocations, type);
            setFilteredLocations(updatedLocations);

            const loc = updatedLocations.find((l) => l._id === item.locationId._id);
            setSelectedLoc(loc || null);

            const shelfRange = typeof loc?.shelf === "string" ? loc.shelf.trim() : "";
            const drawerRange = typeof loc?.drawer === "string" ? loc.drawer.trim() : "";
            populateShelfOptions(shelfRange);
            populateDrawerOptions(drawerRange);
            setSelectedShelf(item.shelf || null);
            setSelectedDrawer(item.drawer || null);
        } else {
            setPreviousLoc({
                location: item.location || "N/A",
                rack: item.rack || "N/A",
                shelf: item.shelf || "N/A",
                drawer: item.drawer || "N/A",
            });
            setOpenPreviousLocText(true);
        }
    };

    // Handles location type change and filters locations accordingly
    const handleLocationTypeChange = (value, clear = true) => {

        // Clear selected location when location type changes
        setSelectedLoc(null);
        setSelectedShelf(null);
        setSelectedDrawer(null);
        setShelfList([]);
        setDrawerList([]);
        
        if (clear) {
            batchChangeHandler({
                "locationId": null,
                "shelf": null,
                "drawer": null
            })
        }

        if (value) {
            // Filter locations by selected location type
            const updatedLocations = filterLocationList(locations, value);

            // Sort locations by Lane and Rack numbers ascending
            updatedLocations.sort((a, b) => {
                const getNum = (str) => {
                    const match = String(str || "").match(/\d+/);
                    return match ? parseInt(match[0], 10) : 0;
                };
                const laneDiff = getNum(b.lane) - getNum(a.lane);
                if (laneDiff !== 0) return laneDiff;
                return getNum(a.rack) - getNum(b.rack);
            });

            setFilteredLocations(updatedLocations);
            setSelectedLocType(value);

            if (value.includes("Shelf")) {
                setOpenShelfField(true);
            } else {
                setOpenShelfField(false);
            } if (value.includes("Drawer")) {
                setOpenDrawerField(true);
            } else {
                setOpenDrawerField(false);
            }
        }
    }

    // Handles location selection and updates shelf and drawer options based on the selected location
    const handleLocationChange = (e) => {
        const locId = e.target.value ? e.target.value._id : null;
        setSelectedLoc(e.target.value || null);

        const loc = e?.target?.value;
        const shelfRange = typeof loc?.shelf === "string" ? loc.shelf.trim() : "";
        const drawerRange = typeof loc?.drawer === "string" ? loc.drawer.trim() : "";


        if (selectedLocType && selectedLocType.includes("Shelf")) {
            populateShelfOptions(shelfRange);
        } if (selectedLocType && selectedLocType.includes("Drawer")) {
            populateDrawerOptions(drawerRange);
        }

        // Reset shelf and drawer selection when location changes     
        setSelectedShelf(null);
        setSelectedDrawer(null);

        batchChangeHandler({
            "locationId": locId,
            "shelf": null,
            "drawer": null
        })
    }

    const filterLocationList = (locs, SelLocType) => {
        const updatedLocations = locs.filter(loc => {
            const locType = loc.locationType ? loc.locationType.toLowerCase() : "";
            return locType === SelLocType.toLowerCase();
        });
        return updatedLocations;
    }

    const populateShelfOptions = (shelfRange) => {
        let shelves = [];

        if (shelfRange) {
            const [startRaw, endRaw] = shelfRange.split("-").map((p) => p.trim());
            const startMatch = startRaw?.match(/^([^\d]*)(\d+)$/i);
            const endMatch = endRaw?.match(/^([^\d]*)(\d+)$/i);

            if (startMatch && endMatch) {
                const prefix = startMatch[1] || endMatch[1] || "";
                const startNumStr = startMatch[2];
                const endNumStr = endMatch[2];

                let startNum = parseInt(startNumStr, 10);
                let endNum = parseInt(endNumStr, 10);

                if (!Number.isNaN(startNum) && !Number.isNaN(endNum)) {
                    if (startNum > endNum) [startNum, endNum] = [endNum, startNum];
                    const padLen = startNumStr.startsWith("0") ? startNumStr.length : 0;

                    for (let n = startNum; n <= endNum; n++) {
                        const numStr = padLen ? String(n).padStart(padLen, "0") : String(n);
                        shelves.push(`${prefix}${numStr}`);
                    }
                }
            }
        }
        setShelfList(shelves);
    }

    const populateDrawerOptions = (drawerRange) => {
        let drawers = [];

        if (drawerRange && drawerRange.includes("-")) {
            const [startRawD, endRawD] = drawerRange.split("-").map(p => p.trim());
            const startMatchD = startRawD?.match(/^([^\d]*)(\d+)$/i);
            const endMatchD = endRawD?.match(/^([^\d]*)(\d+)$/i);

            if (startMatchD && endMatchD) {
                const prefixD = startMatchD[1] || endMatchD[1] || "";
                const startNumStrD = startMatchD[2];
                const endNumStrD = endMatchD[2];

                let startNumD = parseInt(startNumStrD, 10);
                let endNumD = parseInt(endNumStrD, 10);

                if (!Number.isNaN(startNumD) && !Number.isNaN(endNumD)) {
                    if (startNumD > endNumD) [startNumD, endNumD] = [endNumD, startNumD];
                    const padLenD = startNumStrD.startsWith("0") ? startNumStrD.length : 0;

                    for (let n = startNumD; n <= endNumD; n++) {
                        const numStrD = padLenD ? String(n).padStart(padLenD, "0") : String(n);
                        drawers.push(`${prefixD}${numStrD}`);
                    }

                    // Auto-select if only one drawer, otherwise clear current value if it is outside range
                    if (drawers.length === 1) {
                        changeHandler("drawer", drawers[0]);
                    } else if (!drawers.includes(inventoryItem.drawer)) {
                        changeHandler("drawer", "");
                    }
                }
            }
        }
        setDrawerList(drawers);
    }

    return isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" mt={4} mb={4}>
            <CircularProgress />
        </MDBox>
    ) : (
        <MDBox>
            {/* Guide text to help switching from old location to new location */}
            {openPreviousLocText && (
                <MDBox {...commonProps}>
                    <MDTypography variant="body2" color="text">
                        This inventory item is currently assigned to a previous location format.
                        Please select a new location to update it. <br />
                        Previous lane: {previousLoc.location || "N/A"} <br />
                        {previousLoc.rack && (<span>Previous rack: {previousLoc.rack || "N/A"} <br /></span>)}
                        {previousLoc.shelf && (<span>Previous shelf: {previousLoc.shelf || "N/A"} <br /></span>)}
                        {previousLoc.drawer && (<span>Previous drawer: {previousLoc.drawer || "N/A"} <br /></span>)}
                    </MDTypography>
                </MDBox>
            )}
            {/* Location Type Select */}
            < MDBox mt={1} >
                <FormControl fullWidth>
                    <Autocomplete
                        options={locationTypesList}
                        getOptionLabel={(option) => option || ""}
                        isOptionEqualToValue={(option, value) => option === (value || "")}
                        renderInput={(params) => <MDInput {...params} label="Location Type" />}
                        value={selectedLocType}
                        onChange={(_, value) => handleLocationTypeChange(value)} />
                </FormControl>
            </MDBox >

            {/* Location Select */}
            < MDBox {...commonProps} mt={3} >
                <FormControl fullWidth>
                    <Autocomplete
                        options={filteredLocations}
                        groupBy={(option) => option.city || ""}
                        getOptionLabel={(option) => option.locationKey || ""}
                        isOptionEqualToValue={(option, value) => option.locationKey === (value?.locationKey || "")}
                        renderInput={(params) => <MDInput {...params} label="Select Location" />}
                        value={selectedLoc}
                        onChange={(_, value) => handleLocationChange({ target: { value: value } })}
                    />
                </FormControl>
            </MDBox >

            {openShelfField && (
                <Grid container spacing={2}>

                    {/* Shelf */}
                    <Grid item xs={12} lg={openDrawerField ? 6 : 12}>
                        <MDBox {...commonProps}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    options={shelfList}
                                    getOptionLabel={(option) => option || ""}
                                    isOptionEqualToValue={(option, value) => option === (value || "")}
                                    renderInput={(params) => <MDInput {...params} label="Select Shelf" />}
                                    value={selectedShelf}
                                    onChange={(_, value) => {
                                        changeHandler("shelf", value);
                                        setSelectedShelf(value);
                                    }}
                                />
                            </FormControl>
                        </MDBox>
                    </Grid>

                    {openDrawerField && (
                        <Grid item xs={12} lg={6}>
                            <MDBox {...commonProps}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={drawerList}
                                        getOptionLabel={(option) => option || ""}
                                        isOptionEqualToValue={(option, value) => option === (value || "")}
                                        renderInput={(params) => <MDInput {...params} label="Select Drawer" />}
                                        value={selectedDrawer}
                                        onChange={(_, value) => {
                                            changeHandler("drawer", value);
                                            setSelectedDrawer(value);
                                        }}
                                    />
                                </FormControl>
                            </MDBox>
                        </Grid>
                    )}
                </Grid>)
            }
        </MDBox>
    )
}